import { PromotionType } from '../../graphQL/commons'
import {
  ICartProduct,
  IConditionAction,
  IConfig,
  IProduct,
  IPromotion,
  IPromotionDetails,
  IPromotionUiCatalog,
  IPromotionsDetails,
} from '../../interfaces'
import sortByKey from '../sort'
import { getComboPromotionUiDetails } from './getComboPromotionUiDetails'
import { getComplexPromotionUiDetails } from './getComplexPromotionUiDetails'
import { getDirectPromotionUiDetails } from './getDirectPromotionUiDetails'
import { conditionFilterVolumePromotions } from './getVolumePromotionsResume'
import { getVolumePromotionUiDetails } from './getVolumePromotionUiDetails'

export interface IGetMultiPromotionTagArgs {
  config: IConfig
  promotions: IPromotion[] | null
}

export interface IPromotionTypeSeparation {
  volumePromotions: IPromotion[]
  nonVolumePromotions: IPromotion[]
}

interface IPrioritizedPromotionDetails extends IPromotionDetails {
  priority: number
}

export const getMultiPromotionTag = ({
  config,
  promotions,
}: IGetMultiPromotionTagArgs): IPromotionUiCatalog => {
  const {
    texts: { promotions: textsPromotions },
  } = config

  const allVolumePromotions = promotions?.every(
    (promo) => promo.type === PromotionType.VOLUME,
  )

  if (allVolumePromotions) {
    const { multipromoVolumeText: label = '' } = textsPromotions

    return {
      showIcon: true,
      label,
      description: '',
      data: null,
    }
  }

  const {
    multipromoText: label = '',
    multipromoSecondaryText: secondaryText = '',
  } = textsPromotions

  return {
    showIcon: true,
    label,
    secondaryText,
    description: '',
    data: null,
  }
}

type TextFrom = Extract<keyof IPromotionUiCatalog, 'label' | 'description'>
type SecondaryTextFrom = Extract<
  keyof IPromotionUiCatalog,
  'secondaryText' | 'limit'
>
interface IGetMultiPromotionUiArgs {
  config: IConfig
  product: IProduct | ICartProduct
  textFrom?: TextFrom
  secondaryTextFrom?: SecondaryTextFrom
  consolidateVolumePromotions?: boolean
}

type MaybePromotionUiCatalog = IPromotionUiCatalog | undefined

type Actions = Record<
  Partial<PromotionType>,
  (
    promotion: IPromotion,
    product: IProduct | ICartProduct,
    config: IConfig,
  ) => MaybePromotionUiCatalog
>
const actions: Actions = {
  [PromotionType.DIRECT]: (promotion, { price }, config) => {
    return getDirectPromotionUiDetails({
      price: price || 0,
      config,
      promotion,
    })
  },
  [PromotionType.VOLUME]: (promotion, { price, quantity }, config) => {
    return getVolumePromotionUiDetails({
      price: price || 0,
      quantity: quantity || 0,
      config,
      promotion,
    })
  },
  [PromotionType.COMBO]: (promotion, _product, config) => {
    return getComboPromotionUiDetails({
      promotion,
      config,
    })
  },
  [PromotionType.COMPLEX]: (promotion, product, config) => {
    return getComplexPromotionUiDetails({
      promotion,
      config,
    })
  },
  [PromotionType.TOTAL]: () => undefined,
}

const generatePromotionDetails = (
  promotion: IPromotion,
  product: IProduct | ICartProduct,
  config: IConfig,
  textFrom: TextFrom,
  secondaryTextFrom?: SecondaryTextFrom,
): IPromotionDetails | undefined => {
  const actionResult = actions[promotion.type](promotion, product, config)

  if (actionResult) {
    const { saving } = actionResult
    const secondaryText =
      secondaryTextFrom && actionResult[secondaryTextFrom]
        ? String(actionResult[secondaryTextFrom])
        : ''
    const validPromotionDetails = {
      mainText: String(actionResult[textFrom]),
      secondaryText,
      savingText: saving,
    }
    return validPromotionDetails
  }
}

const promotionUiDetailsTransformers: IConditionAction<
  Required<Omit<IGetMultiPromotionUiArgs, 'secondaryTextFrom'>> &
    IPromotionTypeSeparation &
    Pick<IGetMultiPromotionUiArgs, 'secondaryTextFrom'>,
  IPromotionDetails[]
>[] = [
  {
    condition: ({ consolidateVolumePromotions, volumePromotions }) =>
      consolidateVolumePromotions && volumePromotions.length > 1,
    transformAction: ({
      config,
      nonVolumePromotions,
      volumePromotions,
      product,
      textFrom,
      secondaryTextFrom,
    }) => {
      const {
        texts: {
          promotions: { multipromoVolumeText = '' },
        },
      } = config
      const [{ priority: firstVolumePriority = 0 }] = volumePromotions

      const volumePromotionDetails: IPrioritizedPromotionDetails = {
        mainText: multipromoVolumeText,
        secondaryText: '',
        savingText: '',
        priority: firstVolumePriority,
      }

      const nonVolumePromotionDetails = nonVolumePromotions.reduce<
        IPrioritizedPromotionDetails[]
      >((acc, promotion) => {
        const details = generatePromotionDetails(
          promotion,
          product,
          config,
          textFrom,
          secondaryTextFrom,
        )
        if (!details) {
          return acc
        }
        const prioritizedDetails: IPrioritizedPromotionDetails = {
          ...details,
          priority: promotion.priority || 0,
        }
        return [...acc, prioritizedDetails]
      }, [])

      const arrangedPromotions = sortByKey<IPrioritizedPromotionDetails>(
        [volumePromotionDetails, ...nonVolumePromotionDetails],
        'priority',
      )

      return arrangedPromotions.map(
        ({ priority, ...promotionDetails }) => promotionDetails,
      )
    },
  },
  {
    condition: ({ consolidateVolumePromotions, volumePromotions }) =>
      !consolidateVolumePromotions || volumePromotions.length <= 1,
    transformAction: ({ config, product, textFrom, secondaryTextFrom }) => {
      const promotions = product.promotions || []
      const promotionDetails = promotions.reduce<IPromotionDetails[]>(
        (acc, promotion) => {
          const details = generatePromotionDetails(
            promotion,
            product,
            config,
            textFrom,
            secondaryTextFrom,
          )
          return details ? [...acc, details] : acc
        },
        [],
      )
      return promotionDetails
    },
  },
]

const getPromotionsDetails = (
  args: IGetMultiPromotionUiArgs,
): IPromotionDetails[] => {
  const {
    product,
    textFrom = 'description',
    consolidateVolumePromotions = false,
  } = args
  const promotions = product.promotions || []

  const promotionTypeSeparation = promotions.reduce<IPromotionTypeSeparation>(
    (acc, promo) =>
      conditionFilterVolumePromotions(promo)
        ? { ...acc, volumePromotions: [...acc.volumePromotions, promo] }
        : { ...acc, nonVolumePromotions: [...acc.nonVolumePromotions, promo] },
    { volumePromotions: [], nonVolumePromotions: [] },
  )
  return promotionUiDetailsTransformers.reduce<IPromotionDetails[]>(
    (acc, transformer) => {
      const argsForTransformer = {
        ...args,
        ...promotionTypeSeparation,
        textFrom,
        consolidateVolumePromotions,
      }
      return transformer.condition(argsForTransformer)
        ? [...acc, ...transformer.transformAction(argsForTransformer)]
        : acc
    },
    [],
  )
}

export const getMultiPromotionUiDetails = (
  args: IGetMultiPromotionUiArgs,
): IPromotionsDetails => {
  const {
    config,
    product: { promotions },
  } = args
  const { label, secondaryText = '' } = getMultiPromotionTag({
    config,
    promotions,
  })
  const details = getPromotionsDetails(args)

  return {
    title: label,
    secondaryTitle: secondaryText,
    details,
  }
}
